import type { OrderListItem, OrderListLine } from '@agos/api-client/model'
import { isCounterCollection as counterCollection, type FilterChannel } from '@agos/shared'

/**
 * Working out which table an order belongs to, and what colour its tile is.
 *
 * The floor plan is read at a glance from across a room, so the tile's ring
 * colour is the whole interface. Getting the mapping wrong puts an order on the
 * wrong table, which is how a bill reaches the wrong guests.
 *
 * Ported from legacy `pages/table-map.tsx`.
 */

const TABLE_PREFIX = /^Stôl\s+/i

/**
 * Three places a table's name can hide, because three generations of order
 * templates wrote it differently: the column meant for it, the customer-name
 * field with a `Stôl ` prefix, and a bare number.
 */
export function tableKeyForOrder(order: OrderListItem): string | null {
  const tableName = (order as { tableName?: string | null }).tableName
  if (tableName) return tableName.replace(TABLE_PREFIX, '').trim()

  if (order.customerName && TABLE_PREFIX.test(order.customerName)) {
    return order.customerName.replace(TABLE_PREFIX, '').trim()
  }

  const tableNumber = (order as { tableNumber?: string | number | null }).tableNumber
  if (tableNumber !== null && tableNumber !== undefined && `${tableNumber}`.trim() !== '') {
    return `${tableNumber}`.trim()
  }

  return null
}

export type TableStatus = 'free' | 'in_kitchen' | 'ready' | 'served' | 'paid'

export const STATUS_RING: Record<TableStatus, string> = {
  free: 'ring-1 ring-border/40',
  in_kitchen: 'ring-2 ring-amber-500/70 shadow-amber-500/20',
  ready: 'ring-2 ring-blue-500/80 shadow-blue-500/30 animate-pulse',
  served: 'ring-2 ring-emerald-500/60 shadow-emerald-500/20',
  paid: 'ring-1 ring-zinc-500/50 opacity-60',
}

export const STATUS_BG: Record<TableStatus, string> = {
  free: '',
  in_kitchen: 'bg-amber-500/10',
  ready: 'bg-blue-500/15',
  served: 'bg-emerald-500/10',
  paid: 'bg-zinc-500/10',
}

/**
 * What the tile shows.
 *
 * The per-item served progress deliberately outranks the order's own status: a
 * waiter who has already carried everything to the table should not see the
 * tile stuck on *V kuchyni* because the kitchen flow lagged, or because some
 * flows never move an order off `new` at all.
 */
export function statusForOrder(order: OrderListItem | null): TableStatus {
  if (!order) return 'free'
  if (order.status === 'delivered') return 'paid'

  const lines = order.items ?? []
  if (lines.length > 0 && lines.every((line) => servedStatusOf(line) === 'served')) {
    return 'served'
  }

  if (order.status === 'quality_check') return 'ready'
  if (order.status === 'new' || order.status === 'preparing') return 'in_kitchen'

  // Anything else still live — on the way, at the address — has at least been
  // seen by the kitchen.
  return 'served'
}

function servedStatusOf(line: OrderListLine): string {
  return (line as { servedStatus?: string | null }).servedStatus ?? 'pending'
}

/**
 * The one order each table is currently working.
 *
 * A table with two open orders should not happen and does: the newest wins,
 * because it is the one the waiter just took.
 */
export function ordersByTable(orders: OrderListItem[]): Map<string, OrderListItem> {
  const byTable = new Map<string, OrderListItem>()

  for (const order of orders) {
    if (order.type !== 'restaurant') continue
    if (order.status === 'cancelled' || order.status === 'delivered') continue

    const key = tableKeyForOrder(order)
    if (!key) continue

    const existing = byTable.get(key)
    if (!existing || createdAtOf(order) > createdAtOf(existing)) byTable.set(key, order)
  }

  return byTable
}

function createdAtOf(order: OrderListItem): number {
  return new Date(order.createdAt ?? 0).getTime()
}

/**
 * Pickup orders, which belong to no table. Ready first — somebody is standing
 * at the counter — then oldest.
 */
/**
 * On the premises and not at a table - §9.393.
 *
 * <p><b>`isCounterCollection` is this rule's canonical reader</b> and it is exported from
 * `@agos/shared`: the dispatch board asks it, and this screen spelled it as `type === 'bar'`, so a
 * tableless on-premise channel a franchise invents had invisible open orders in the room.
 *
 * <p><b>The wrapper exists for the fallback, and writing it without one failed seven tests.</b>
 * `isCounterCollection` answers **false** for a channel it cannot resolve - deliberately, for the
 * board, where an unresolvable channel must not be sorted as though somebody were waiting at the
 * counter. Here an empty list is the ordinary state of a screen whose channels have not arrived yet,
 * and answering false for every order empties the counter queue. So an unknown channel keeps the slug
 * check, and `bar` has both flags, which is what makes the two answers agree for it.
 */
function isCounterOrder(type: string, channels: readonly FilterChannel[]): boolean {
  const known = channels.some((channel) => channel.slug === type)
  return known ? counterCollection(type, channels) : type === 'bar'
}

export function pickupOrders(
  orders: OrderListItem[],
  channels: readonly FilterChannel[] = [],
): OrderListItem[] {
  return orders
    .filter(
      (order) =>
        isCounterOrder(order.type ?? '', channels) &&
        order.status !== 'cancelled' &&
        order.status !== 'delivered',
    )
    .sort((left, right) => {
      const byReady = (order: OrderListItem) => (order.status === 'quality_check' ? 0 : 1)
      if (byReady(left) !== byReady(right)) return byReady(left) - byReady(right)
      return createdAtOf(left) - createdAtOf(right)
    })
}

/**
 * Everything open in the room: a card per occupied table, then the counter collections - §9.319, the
 * owner's Q9.
 *
 * **The owner:** *"In otvorené stoly also show Items for osobný odber."* **A missed port.** Legacy's
 * open-tables list is
 * `filter(o => (o.type === "restaurant" || o.type === "bar") && …)` (`restaurant.tsx:1007`) and
 * labels the pickup rows *Osobný odber* where a table would name itself (`:109`). AGOS built the
 * screen on `ordersByTable` alone, which skips anything that is not `restaurant`, so a counter order
 * and its items were on the **table map** and nowhere on the screen a waiter watches.
 *
 * Tables first and in the floor plan's own order, then the counter - a table is a person sitting in
 * the room, and the counter queue is already sorted ready-first by `pickupOrders`.
 *
 * **`heading` rather than `tableName`**, because a pickup order has no table and calling the label
 * after the thing it is not is how the hard-coded `Stôl` prefix would come back.
 */
export interface OpenRoomEntry {
  /** Stable across renders: the order's own id, not the heading, which two pickups can share. */
  key: number
  heading: string
  order: OrderListItem
  /** A counter collection, which takes no table and adds to itself differently. */
  isPickup: boolean
}

export function openRoomOrders(
  orders: OrderListItem[],
  pickupLabel: string,
  channels: readonly FilterChannel[] = [],
): OpenRoomEntry[] {
  const tables = [...ordersByTable(orders).entries()].map(([heading, order]) => ({
    key: order.id ?? 0,
    heading,
    order,
    isPickup: false,
  }))
  const counter = pickupOrders(orders, channels).map((order) => ({
    key: order.id ?? 0,
    heading: pickupLabel,
    order,
    isPickup: true,
  }))
  return [...tables, ...counter]
}

/** `Ján Novák` → `JN`. The badge on an occupied tile. */
export function initialsOf(name: string | null | undefined): string {
  if (!name) return '?'
  return (
    name
      .split(' ')
      .map((word) => word[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  )
}

/** The floor plan's coordinate space. Tiles are positioned as percentages of it. */
export const PLANE_WIDTH = 1000
export const PLANE_HEIGHT = 600

/** Keeps a dragged tile inside the plane, whatever the pointer did. */
export function clampToPlane(
  x: number,
  y: number,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(PLANE_WIDTH - width, x)),
    y: Math.max(0, Math.min(PLANE_HEIGHT - height, y)),
  }
}
